import api from "../../../api";
import { Roles } from "../../../models/role-model";
import {
  AccessGroup,
  AccessPoint,
  PropertyDetails,
  WizardUnit,
  WizardUser,
  resolveGroupRoleId,
  resolveInviteRoleId,
  INVITE_ROLES,
  WEEK_DAY_NAMES,
} from "../types";

export interface EditEntitySnapshot {
  accessPointDocumentIds: string[];
  gateDocumentIds: string[];
  unitDocumentIds: string[];
  groupDocumentIds: string[];
  userIds: number[];
}

export const isAccessGroupCompleteForSync = (group: AccessGroup) =>
  group.name.trim().length > 0 &&
  group.points.length > 0 &&
  group.roles.length > 0;

export const buildEditSnapshot = (
  accessPoints: AccessPoint[],
  units: WizardUnit[],
  groups: AccessGroup[],
  users: WizardUser[],
): EditEntitySnapshot => ({
  accessPointDocumentIds: accessPoints
    .map((point) => point.documentId)
    .filter((id): id is string => Boolean(id)),
  gateDocumentIds: accessPoints.flatMap((point) =>
    point.gates
      .map((gate) => gate.documentId)
      .filter((id): id is string => Boolean(id)),
  ),
  unitDocumentIds: units
    .map((unit) => unit.documentId)
    .filter((id): id is string => Boolean(id)),
  groupDocumentIds: groups
    .map((group) => group.documentId)
    .filter((id): id is string => Boolean(id)),
  userIds: users
    .map((user) => user.strapiId)
    .filter((id): id is number => Number.isFinite(id)),
});

interface SyncPropertyEditParams {
  propertyId: number;
  companyId: number | undefined;
  prop: PropertyDetails;
  accessPoints: AccessPoint[];
  units: WizardUnit[];
  groups: AccessGroup[];
  users: WizardUser[];
  roles: Roles[];
  snapshot: EditEntitySnapshot;
  setStage: (message: string) => void;
}

const buildGroupPayload = (
  group: AccessGroup,
  companyId: number | undefined,
  propertyId: number,
  accessPointIdByLocalId: Record<string, number>,
  roles: Roles[],
) => {
  const groupAccessPointIds = group.points
    .map((localId) => accessPointIdByLocalId[localId])
    .filter((id): id is number => Number.isFinite(id));
  const activeDayNames = WEEK_DAY_NAMES.filter((_, i) => group.days[i]);
  const activePhotoKeys = Object.entries(group.photos)
    .filter(([, on]) => on)
    .map(([key]) => key);
  const configuration = groupAccessPointIds.map((apId) => ({
    id: apId,
    week_days: activeDayNames,
    start_time: `${group.from}:00`,
    end_time: `${group.to}:00`,
    exit_restriction: group.exitRestricted
      ? `${group.exitFrom}:00`
      : undefined,
    antipassback: 0,
    description: group.name,
    name: group.name,
    register: true,
    photos: activePhotoKeys,
    printable_pass: false,
  }));

  return {
    data: {
      name: group.name,
      company: companyId ?? null,
      properties: [propertyId],
      access_points: groupAccessPointIds,
      role_permissions_users: group.roles
        .map((role) => resolveGroupRoleId(roles, role))
        .filter((id): id is number => id != null),
      configuration,
      banned_users: [],
      unit_access_group: false,
    },
  };
};

export async function syncPropertyEdit({
  propertyId,
  companyId,
  prop,
  accessPoints,
  units,
  groups,
  users,
  roles,
  snapshot,
  setStage,
}: SyncPropertyEditParams): Promise<void> {
  setStage("Guardando puntos de acceso...");
  const accessPointIdByLocalId: Record<string, number> = {};
  const keptAccessPointDocumentIds = new Set<string>();
  const keptGateDocumentIds = new Set<string>();

  for (const point of accessPoints) {
    const layoutPin = prop.layoutPins.find(
      (pin) => pin.accessPointId === point.id,
    );
    const pointPayload = {
      data: {
        name: point.nombre,
        vanue_name: point.calleOAvenida,
        longitude: Number(point.longitud) || undefined,
        latitude: Number(point.latitud) || undefined,
        property: propertyId,
        time_zone_custom: Number(prop.tz) || undefined,
        layout_coordinates: layoutPin
          ? {
              xPercent: layoutPin.xPercent,
              yPercent: layoutPin.yPercent,
            }
          : null,
      },
    };

    let apId = point.strapiId;
    if (point.documentId && apId) {
      await api.updateAccessPoint(point.documentId, pointPayload);
      keptAccessPointDocumentIds.add(point.documentId);
    } else {
      const response = await api.createAccessPoint(pointPayload);
      apId = Number(response?.data?.id);
    }

    if (!apId || !Number.isFinite(apId)) {
      throw new Error(
        `No se pudo guardar el punto "${point.nombre || "sin nombre"}".`,
      );
    }
    accessPointIdByLocalId[point.id] = apId;

    for (const gate of point.gates) {
      const gatePayload = {
        data: {
          name: gate.name,
          type: (gate.type === "Entrada" ? "entrance" : "exit") as
            | "entrance"
            | "exit",
          access_point: apId,
        },
      };

      if (gate.documentId) {
        await api.updateGate(gate.documentId, gatePayload);
        keptGateDocumentIds.add(gate.documentId);
      } else {
        await api.createGate(gatePayload);
      }
    }
  }

  for (const gateDocumentId of snapshot.gateDocumentIds) {
    if (!keptGateDocumentIds.has(gateDocumentId)) {
      await api.deleteGate(gateDocumentId);
    }
  }

  for (const accessPointDocumentId of snapshot.accessPointDocumentIds) {
    if (!keptAccessPointDocumentIds.has(accessPointDocumentId)) {
      await api.deleteAccessPoint(accessPointDocumentId);
    }
  }

  const accessPointIds = Object.values(accessPointIdByLocalId);

  setStage("Guardando unidades...");
  const unitIdByLocalId: Record<string, number> = {};
  const keptUnitDocumentIds = new Set<string>();

  for (const unit of units) {
    const unitPayload = {
      data: {
        name: unit.locked ? `Admin ${prop.name}` : unit.name,
        property: propertyId,
        access_points: accessPointIds,
        is_admin: unit.locked ? true : false,
      },
    };

    if (unit.documentId && unit.strapiId) {
      await api.updateUnit(unit.documentId, unitPayload);
      unitIdByLocalId[unit.id] = unit.strapiId;
      keptUnitDocumentIds.add(unit.documentId);
    } else {
      const response = await api.createUnit(unitPayload);
      unitIdByLocalId[unit.id] = Number(response?.data?.id);
    }
  }

  for (const unitDocumentId of snapshot.unitDocumentIds) {
    if (!keptUnitDocumentIds.has(unitDocumentId)) {
      await api.deleteUnit(unitDocumentId);
    }
  }

  const adminUnit = units.find((unit) => unit.locked);
  const adminUnitId = adminUnit ? unitIdByLocalId[adminUnit.id] : undefined;

  setStage("Guardando grupos de acceso...");
  const completeGroups = groups.filter(isAccessGroupCompleteForSync);
  const keptGroupDocumentIds = new Set<string>();

  for (const group of completeGroups) {
    const payload = buildGroupPayload(
      group,
      companyId,
      propertyId,
      accessPointIdByLocalId,
      roles,
    );

    if (group.documentId) {
      await api.updateAccessGroup(group.documentId, payload);
      keptGroupDocumentIds.add(group.documentId);
    } else {
      const response = await api.createAccessGroup(payload);
      const groupId = response?.data?.id;
      if (groupId && prop.settings.has_requirements_permissions) {
        for (const field of group.fields) {
          if (!field.label.trim()) continue;
          const dataType = field.type === "selector" ? "selector" : "string";
          await api.createRequirementType({
            data: {
              name: field.label,
              description: field.label,
              data_type: dataType,
              required: field.required,
              access_key: false,
              transaction: true,
              access_groups: [groupId],
              url: "",
              ...(dataType === "selector"
                ? {
                    selector_data: field.options
                      .split(",")
                      .map((opt) => opt.trim())
                      .filter(Boolean),
                  }
                : {}),
            },
          });
        }
      }
    }
  }

  for (const groupDocumentId of snapshot.groupDocumentIds) {
    if (!keptGroupDocumentIds.has(groupDocumentId)) {
      await api.deleteAccessGroup(groupDocumentId);
    }
  }

  setStage("Guardando usuarios...");
  const keptUserIds = new Set<number>();
  const usersToSave = users.filter((user) => user.name.trim());

  for (const user of usersToSave) {
    const roleMeta = INVITE_ROLES.find((role) => role.key === user.role);
    const targetUnit = units.find((unit) => unit.name === user.unit);
    const unitId =
      user.role === "Administración"
        ? adminUnitId
        : roleMeta?.needsUnit && targetUnit
          ? unitIdByLocalId[targetUnit.id]
          : undefined;
    const accessPointsForUser = roleMeta?.needsPoints
      ? user.points
          .map((localId) => accessPointIdByLocalId[localId])
          .filter((id): id is number => Number.isFinite(id))
      : accessPointIds;
    const roleId = resolveInviteRoleId(roles, user.role);
    const phone = `${user.phoneCountryCode}${user.phoneNumber}`;

    if (user.strapiId) {
      await api.updateUser(user.strapiId, {
        name: user.name,
        username: user.name,
        email: user.email,
        phone,
        unit: unitId ?? null,
        property: propertyId,
        ...(companyId ? { company: companyId } : {}),
        access_points: accessPointsForUser,
        ...(roleId != null ? { role: roleId } : {}),
      });
      keptUserIds.add(user.strapiId);
    } else {
      if (user.email.trim()) {
        const existing = await api.getUsersByEmail(user.email);
        if (existing.length > 0) {
          const err = new Error(
            `Ya existe un usuario con el correo ${user.email}`,
          );
          (err as Error & { code?: string }).code = "DUPLICATE_EMAIL";
          throw err;
        }
      }

      const response = await api.createUser({
        name: user.name,
        username: user.name,
        email: user.email,
        phone,
        unit: unitId,
        property: propertyId,
        company: companyId,
        access_points: accessPointsForUser,
        blocked: false,
        license: 1,
        password: "temporal",
      });
      const createdId = response?.user?.id;
      const confirmationToken = response?.confirmation_token
        ? String(response.confirmation_token)
        : "";

      if (!createdId) {
        throw new Error(
          `No se pudo crear el usuario "${user.name || user.email}".`,
        );
      }
      if (roleId == null) {
        throw new Error(
          `No se pudo asignar el rol "${user.role}" a "${user.name || user.email}".`,
        );
      }
      await api.addUserRole(createdId, roleId);

      if (!confirmationToken) {
        throw new Error(
          `El usuario "${user.email}" se creó sin token de confirmación; no se pudo enviar el correo.`,
        );
      }
      await api.sendConfirmationEmail({
        email: user.email,
        confirmation_token: confirmationToken,
      });

      keptUserIds.add(createdId);
    }
  }

  for (const userId of snapshot.userIds) {
    if (!keptUserIds.has(userId)) {
      await api.deleteUser(userId);
    }
  }
}
